import { useCallback, useEffect, useRef, useState } from "react";

const ARC_START_DEG = 225;
const ARC_SWEEP_DEG = 270;
const SVG_SIZE = 360;
const CENTER = SVG_SIZE / 2;
const RADIUS = 126;
const HANDLE_RADIUS = 14;
const HANDLE_BORDER_WIDTH = 5;
const HANDLE_SIZE = (HANDLE_RADIUS * 2) + 12;
const TICK_COUNT = 29;

const normalizeAngle = (angle) => {
  if (angle < 0) {
    return angle + 360;
  }

  return angle;
};

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians)),
  };
};

const describeArc = (startAngle, endAngle) => {
  const start = polarToCartesian(CENTER, CENTER, RADIUS, endAngle);
  const end = polarToCartesian(CENTER, CENTER, RADIUS, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const KEYBOARD_STEP = 1;
const KEYBOARD_PAGE_STEP = 5;

const CircularSetpointControl = ({
  heatMin,
  heatMax,
  heatValue,
  onHeatChange,
  coolMin,
  coolMax,
  coolValue,
  onCoolChange,
  controlMode,
  hideInstructions = false,
  readOnly = false,
  "data-testid": dataTestId = "circular-setpoint-control",
}) => {
  const svgRef = useRef(null);
  const [activeHandle, setActiveHandle] = useState(null);

  const isHeatEnabled = controlMode === "HEAT" || controlMode === "HEAT_AND_COOL";
  const isCoolEnabled =
    controlMode === "COOL" || controlMode === "DRY" || controlMode === "HEAT_AND_COOL";

  const domainMin = Math.min(heatMin, coolMin);
  const domainMax = Math.max(heatMax, coolMax);
  const domainRange = domainMax - domainMin || 1;

  const valueToAngle = useCallback((value) => {
    const percent = (value - domainMin) / domainRange;
    return ARC_START_DEG + (percent * ARC_SWEEP_DEG);
  }, [domainMin, domainRange]);

  const pointerToValue = useCallback((clientX, clientY, handleType) => {
    const svgBounds = svgRef.current?.getBoundingClientRect();

    if (!svgBounds) {
      return null;
    }

    const scaleX = SVG_SIZE / svgBounds.width;
    const scaleY = SVG_SIZE / svgBounds.height;
    const pointX = (clientX - svgBounds.left) * scaleX;
    const pointY = (clientY - svgBounds.top) * scaleY;
    const angle = normalizeAngle((Math.atan2(pointY - CENTER, pointX - CENTER) * 180) / Math.PI + 90);
    const rangeMin = handleType === "HEAT" ? heatMin : coolMin;
    const rangeMax = handleType === "HEAT" ? heatMax : coolMax;
    const minAngle = valueToAngle(rangeMin);
    const maxAngle = valueToAngle(rangeMax);
    const candidateAngles = [angle, angle + 360];
    const closestAngle = candidateAngles.reduce((closest, candidate) => {
      const candidateDistance =
        candidate < minAngle ? minAngle - candidate : candidate > maxAngle ? candidate - maxAngle : 0;
      const closestDistance =
        closest < minAngle ? minAngle - closest : closest > maxAngle ? closest - maxAngle : 0;

      return candidateDistance < closestDistance ? candidate : closest;
    }, candidateAngles[0]);
    const clampedAngle = clamp(closestAngle, minAngle, maxAngle);
    const percent = (clampedAngle - ARC_START_DEG) / ARC_SWEEP_DEG;

    return Math.round(domainMin + (percent * domainRange));
  }, [coolMax, coolMin, domainMin, domainRange, heatMax, heatMin, valueToAngle]);

  useEffect(() => {
    if (!activeHandle) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const nextValue = pointerToValue(event.clientX, event.clientY, activeHandle);

      if (nextValue === null) {
        return;
      }

      if (activeHandle === "HEAT" && isHeatEnabled) {
        onHeatChange(clamp(nextValue, heatMin, heatMax));
      }

      if (activeHandle === "COOL" && isCoolEnabled) {
        onCoolChange(clamp(nextValue, coolMin, coolMax));
      }
    };

    const clearActiveHandle = () => {
      setActiveHandle(null);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", clearActiveHandle);
    window.addEventListener("pointercancel", clearActiveHandle);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", clearActiveHandle);
      window.removeEventListener("pointercancel", clearActiveHandle);
    };
  }, [
    activeHandle,
    coolMax,
    coolMin,
    heatMax,
    heatMin,
    isCoolEnabled,
    isHeatEnabled,
    onCoolChange,
    onHeatChange,
    pointerToValue,
  ]);

  const handlePointerDown = (handleType) => (event) => {
    const isDisabled = readOnly || (handleType === "HEAT" ? !isHeatEnabled : !isCoolEnabled);

    if (isDisabled) {
      return;
    }

    event.preventDefault();
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    setActiveHandle(handleType);
    const nextValue = pointerToValue(event.clientX, event.clientY, handleType);

    if (nextValue === null) {
      return;
    }

    if (handleType === "HEAT") {
      onHeatChange(clamp(nextValue, heatMin, heatMax));
      return;
    }

    onCoolChange(clamp(nextValue, coolMin, coolMax));
  };

  const buildTicks = () => {
    const ticks = [];

    for (let index = 0; index < TICK_COUNT; index += 1) {
      const percent = index / (TICK_COUNT - 1);
      const angle = ARC_START_DEG + (percent * ARC_SWEEP_DEG);
      const innerPoint = polarToCartesian(CENTER, CENTER, RADIUS - 12, angle);
      const outerPoint = polarToCartesian(CENTER, CENTER, RADIUS, angle);

      ticks.push(
        <line
          key={angle}
          x1={innerPoint.x}
          y1={innerPoint.y}
          x2={outerPoint.x}
          y2={outerPoint.y}
          className="stroke-slate-300 dark:stroke-[#7993B8]"
          strokeWidth={4}
          strokeLinecap="round"
        />
      );
    }

    return ticks;
  };

  const heatAngle = valueToAngle(heatValue);
  const coolAngle = valueToAngle(coolValue);
  const heatPoint = polarToCartesian(CENTER, CENTER, RADIUS, heatAngle);
  const coolPoint = polarToCartesian(CENTER, CENTER, RADIUS, coolAngle);
  const heatHandleLeft = `${(heatPoint.x / SVG_SIZE) * 100}%`;
  const heatHandleTop = `${(heatPoint.y / SVG_SIZE) * 100}%`;
  const coolHandleLeft = `${(coolPoint.x / SVG_SIZE) * 100}%`;
  const coolHandleTop = `${(coolPoint.y / SVG_SIZE) * 100}%`;

  const updateHandleValue = (handleType, nextValue) => {
    if (handleType === "HEAT") {
      onHeatChange(clamp(nextValue, heatMin, heatMax));
      return;
    }

    onCoolChange(clamp(nextValue, coolMin, coolMax));
  };

  const handleKeyDown = (handleType) => (event) => {
    const isDisabled = readOnly || (handleType === "HEAT" ? !isHeatEnabled : !isCoolEnabled);

    if (isDisabled) {
      return;
    }

    const currentValue = handleType === "HEAT" ? heatValue : coolValue;
    const minValue = handleType === "HEAT" ? heatMin : coolMin;
    const maxValue = handleType === "HEAT" ? heatMax : coolMax;
    const keyToDelta = {
      ArrowUp: KEYBOARD_STEP,
      ArrowRight: KEYBOARD_STEP,
      ArrowDown: -KEYBOARD_STEP,
      ArrowLeft: -KEYBOARD_STEP,
      PageUp: KEYBOARD_PAGE_STEP,
      PageDown: -KEYBOARD_PAGE_STEP,
    };

    if (event.key === "Home") {
      event.preventDefault();
      updateHandleValue(handleType, minValue);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      updateHandleValue(handleType, maxValue);
      return;
    }

    const delta = keyToDelta[event.key];

    if (delta === undefined) {
      return;
    }

    event.preventDefault();
    updateHandleValue(handleType, currentValue + delta);
  };

  return (
    <div className="w-full" data-testid={dataTestId}>
      <div className="mx-auto flex w-full max-w-[420px] flex-col items-center">
        <div className="relative w-full max-w-[420px] aspect-square select-none touch-none">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
            className="h-full w-full overflow-visible"
            role="img"
            aria-label="Zone setpoint control"
            data-testid={`${dataTestId}-svg`}
          >
            <path
              d={describeArc(ARC_START_DEG, ARC_START_DEG + ARC_SWEEP_DEG)}
              className="fill-none stroke-slate-200 dark:stroke-[#476487]"
              strokeWidth={16}
              strokeLinecap="round"
            />
            {buildTicks()}
          </svg>

          <div className="absolute inset-0">
            {isHeatEnabled && (
              <button
                type="button"
                role="slider"
                aria-label="Heat setpoint"
                aria-valuemin={heatMin}
                aria-valuemax={heatMax}
                aria-valuenow={heatValue}
                aria-valuetext={`${heatValue} degrees`}
                aria-disabled={readOnly || !isHeatEnabled}
                tabIndex={!readOnly && isHeatEnabled ? 0 : -1}
                onPointerDown={handlePointerDown("HEAT")}
                onKeyDown={handleKeyDown("HEAT")}
                data-testid={`${dataTestId}-heat-handle`}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full shadow-sm cursor-grab active:cursor-grabbing focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#FF9500] dark:focus-visible:outline-[#FFD166]"
                style={{
                  left: heatHandleLeft,
                  top: heatHandleTop,
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  backgroundColor: "#fe5b26",
                  borderWidth: HANDLE_BORDER_WIDTH,
                  borderStyle: "solid",
                  borderColor: "white",
                  touchAction: "none",
                }}
              />
            )}
            {isCoolEnabled && (
              <button
                type="button"
                role="slider"
                aria-label="Cool setpoint"
                aria-valuemin={coolMin}
                aria-valuemax={coolMax}
                aria-valuenow={coolValue}
                aria-valuetext={`${coolValue} degrees`}
                aria-disabled={readOnly || !isCoolEnabled}
                tabIndex={!readOnly && isCoolEnabled ? 0 : -1}
                onPointerDown={handlePointerDown("COOL")}
                onKeyDown={handleKeyDown("COOL")}
                data-testid={`${dataTestId}-cool-handle`}
                className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full shadow-sm cursor-grab active:cursor-grabbing focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4C9AFF] dark:focus-visible:outline-[#A9D1FF]"
                style={{
                  left: coolHandleLeft,
                  top: coolHandleTop,
                  width: HANDLE_SIZE,
                  height: HANDLE_SIZE,
                  backgroundColor: "#3b82f6",
                  borderWidth: HANDLE_BORDER_WIDTH,
                  borderStyle: "solid",
                  borderColor: "white",
                  touchAction: "none",
                }}
              />
            )}
          </div>

          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="flex items-end gap-4 sm:gap-8">
              <div className={`text-center ${isHeatEnabled ? "opacity-100" : "opacity-45"}`}>
                <div className="text-[2rem] font-bold leading-none text-[#fe5b26] sm:text-[3rem]" data-testid={`${dataTestId}-heat-value`}>
                  {heatValue}°
                </div>
                <div className="mt-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#fe5b26] sm:text-base">
                  Heat
                </div>
              </div>
              <div className={`text-center ${isCoolEnabled ? "opacity-100" : "opacity-45"}`}>
                <div className="text-[2rem] font-bold leading-none text-[#3b82f6] dark:text-[#6FA8FF] sm:text-[3rem]" data-testid={`${dataTestId}-cool-value`}>
                  {coolValue}°
                </div>
                <div className="mt-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#3b82f6] dark:text-[#6FA8FF] sm:text-base">
                  Cool
                </div>
              </div>
            </div>
          </div>

          {!hideInstructions && (
            <div className="pointer-events-none absolute inset-x-0 bottom-[12%] px-6 text-center text-xs font-medium text-slate-500 dark:text-slate-300 sm:text-sm">
              Drag the colored handles to adjust setpoints
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CircularSetpointControl;
